import type { Asset, WatchHistory } from '../../../data/asset';
import { convertMediaTypeToRhinoAssetType } from '../../../data/compat/rhinoAssetTypeToMediaType';
import { tvpil } from '../../../data/compat/tvpil';
import type {
  WatchedAsset,
  WatchedEpisodeAsset,
  WatchedMovieAsset,
  WatchedProgramAsset,
  WatchedRecordingAsset,
} from '../../../data/continueWatching/types';
import type { KalturaWatchedAsset } from '../../composed';
import { KalturaAssetHistory } from '../../objects';
import { fromKalturaBoolean } from '../../utils';
import { processPhoenixImages } from '../processing';
import { processVodAssetMetas } from '../vod';

const processors = {
  program: processProgram,
  movie: processMovie,
  episode: processEpisode,
};

export function processWatchedAsset({
  assetHistory,
  watchedAsset,
}: {
  assetHistory: KalturaAssetHistory;
  watchedAsset: KalturaWatchedAsset;
}): WatchedAsset {
  const type = convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[watchedAsset.type]);

  if (watchedAsset.recordingId) {
    return processRecording(watchedAsset, assetHistory);
  }

  const process = processors[type as keyof typeof processors] ?? processProgram;

  return process(watchedAsset, assetHistory);
}

function processBaseAsset(asset: KalturaWatchedAsset): Asset {
  return {
    id: asset.id,
    title: asset.name,
    ...(asset.tags['ParentalRating'] && {
      parentalRating: Number(asset.tags['ParentalRating'].objects[0].value),
    }),
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    type: convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[asset.type]),
    images: processPhoenixImages(asset.images),
  };
}

function processMosaic(asset: KalturaWatchedAsset): Pick<WatchedProgramAsset, 'hasMosaic' | 'mosaicExternalId'> {
  const mosaicInfo = asset.tags?.['MosaicInfo'];

  return {
    hasMosaic: fromKalturaBoolean(asset.metas.IsMosaicEvent),
    ...(mosaicInfo && { mosaicExternalId: asset.tags.MosaicInfo.objects[0]?.value.split('=')[1] }),
  };
}

function processHistory(history: KalturaAssetHistory): WatchHistory {
  return {
    position: history.position,
    duration: history.duration,
    hasFinishedWatching: history.finishedWatching,
  };
}

function processProgram(asset: KalturaWatchedAsset, history: KalturaAssetHistory): WatchedProgramAsset {
  return {
    ...processBaseAsset(asset),
    ...processHistory(history),
    ...processMosaic(asset),
    channelId: asset.linearAssetId,
    endDate: asset.endDate * 1000,
    ...(asset.metas['EpisodeName'] && {
      episodeName: String(asset.metas['EpisodeName'].value),
    }),
    ...(asset.metas['EpisodeNumber'] && {
      episodeNumber: Number(asset.metas['EpisodeNumber'].value),
    }),
    ...(asset.metas['SeasonNumber'] && {
      seasonNumber: Number(asset.metas['SeasonNumber'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    ...(asset.tags['SeriesName'] && {
      seriesName: String(asset.tags['SeriesName'].objects[0].value),
    }),
    startDate: asset.startDate * 1000,
    type: 'program',
  };
}

function processMovie(asset: KalturaWatchedAsset, history: KalturaAssetHistory): WatchedMovieAsset {
  return {
    ...processBaseAsset(asset),
    ...processHistory(history),
    ...processVodAssetMetas(asset.metas),
    ...(asset.tags['BillingId'] && {
      billingPlans: asset.tags['BillingId'].objects.map(({ value }) => value),
    }),
    type: 'movie',
  };
}

function processEpisode(asset: KalturaWatchedAsset, history: KalturaAssetHistory): WatchedEpisodeAsset {
  return {
    ...processBaseAsset(asset),
    ...processHistory(history),
    ...processVodAssetMetas(asset.metas),
    ...(asset.tags['BillingId'] && {
      billingPlans: asset.tags['BillingId'].objects.map(({ value }) => value),
    }),
    ...(asset.metas['EpisodeName'] && {
      episodeName: String(asset.metas['EpisodeName'].value),
    }),
    ...(asset.metas['EpisodeNumber'] && {
      episodeNumber: Number(asset.metas['EpisodeNumber'].value),
    }),
    ...(asset.metas['SeasonNumber'] && {
      seasonNumber: Number(asset.metas['SeasonNumber'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    ...(asset.tags['SeriesName'] && {
      seriesName: String(asset.tags['SeriesName'].objects[0].value),
    }),
    type: 'episode',
  };
}

function processRecording(asset: KalturaWatchedAsset, history: KalturaAssetHistory): WatchedRecordingAsset {
  return {
    ...processBaseAsset(asset),
    ...processHistory(history),
    ...processMosaic(asset),
    channelId: asset.linearAssetId,
    endDate: asset.endDate * 1000,
    ...(asset.metas['EpisodeName'] && {
      episodeName: String(asset.metas['EpisodeName'].value),
    }),
    ...(asset.metas['EpisodeNumber'] && {
      episodeNumber: Number(asset.metas['EpisodeNumber'].value),
    }),
    ...(asset.metas['SeasonNumber'] && {
      seasonNumber: Number(asset.metas['SeasonNumber'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    recordingId: Number(asset.recordingId),
    ...(asset.tags['SeriesName'] && {
      seriesName: String(asset.tags['SeriesName'].objects[0].value),
    }),
    startDate: asset.startDate * 1000,
    type: 'recording',
  };
}
